/* eslint no-console: ["error", { allow: ["warn", "error", "log"] }] */
import { useEffect } from 'react';

import {
  createBrowserRouter,
  Outlet,
  RouterProvider as ReactRouterProvider,
  useLocation,
} from 'react-router-dom';

import { MainLayout } from '@app/layouts/MainLayout';

import { DashboardPage } from '@pages/DashboardPage';
import { LandingPage, LandingRouteFrame } from '@pages/LandingPage';
import { LoginPage } from '@pages/LoginPage';
import { NotFoundPage } from '@pages/NotFoundPage';
import { PracticePage } from '@pages/PracticePage';
import { StaticPageExample } from '@pages/StaticPageExample';

import { ROUTES } from '@shared/config/routes';

const routeBodyBackgroundClasses = ['bg-background-1', 'bg-background-2'] as const;

const AppRouteFrame = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const bodyBackgroundClass =
      pathname === ROUTES.DASHBOARD ? 'bg-background-1' : 'bg-background-2';
    const classesToRemove = routeBodyBackgroundClasses.filter(
      (className) => className !== bodyBackgroundClass
    );

    document.body.classList.remove(...classesToRemove);
    document.body.classList.add(bodyBackgroundClass);

    return () => {
      document.body.classList.remove(bodyBackgroundClass);
    };
  }, [pathname]);

  return <Outlet />;
};

const router = createBrowserRouter([
  {
    element: <AppRouteFrame />,
    children: [
      {
        element: <LandingRouteFrame />,
        children: [
          {
            path: ROUTES.HOME,
            element: <LandingPage />,
          },
          {
            path: ROUTES.TERMS_PRIVACY,
            element: <StaticPageExample />,
          },
          {
            path: ROUTES.LEGAL,
            element: <StaticPageExample />,
          },
        ],
      },
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.DASHBOARD,
        element: (
          <MainLayout>
            <DashboardPage />
          </MainLayout>
        ),
      },
      {
        path: ROUTES.PRACTICE,
        element: (
          <MainLayout headerMode="overlay">
            <PracticePage />
          </MainLayout>
        ),
      },
      {
        path: ROUTES.QUANT,
        element: (
          <MainLayout
            headerMode="overlay"
            breadcrumbs={[
              { label: 'GMAT Prep', href: ROUTES.DASHBOARD },
              { label: 'Quantitative Reasoning' },
            ]}
            primaryAction={{
              label: 'Start Section Practice',
              icon: 'start',
              onClick: () => {
                console.log('Start practice clicked');
              },
            }}
          >
            <PracticePage topic="quant" />
          </MainLayout>
        ),
      },
      {
        path: ROUTES.VERBAL,
        element: (
          <MainLayout
            headerMode="overlay"
            breadcrumbs={[
              { label: 'GMAT Prep', href: ROUTES.DASHBOARD },
              { label: 'Verbal Reasoning' },
            ]}
            primaryAction={{
              label: 'Start Section Practice',
              icon: 'start',
              onClick: () => {
                console.log('Start practice clicked');
              },
            }}
          >
            <PracticePage topic="verbal" />
          </MainLayout>
        ),
      },
      {
        path: ROUTES.DATA,
        element: (
          <MainLayout
            headerMode="overlay"
            breadcrumbs={[
              { label: 'GMAT Prep', href: ROUTES.DASHBOARD },
              { label: 'Data Insights' },
            ]}
            primaryAction={{
              label: 'Start Section Practice',
              icon: 'start',
              onClick: () => {
                console.log('Start practice clicked');
              },
            }}
          >
            <PracticePage topic="data" />
          </MainLayout>
        ),
      },
      {
        element: <LandingRouteFrame />,
        children: [
          {
            path: ROUTES.NOT_FOUND,
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);

export const RouterProvider = () => {
  return <ReactRouterProvider router={router} />;
};
